import classnames from "classnames";
import { ClassValue } from "classnames/types";
import { BulmaNavButtons } from "Components/BulmaNavButtons";
import { useDocumentTitle } from "Components/Hooks";
import { InfoModal } from "Components/InfoModal";
import { Section } from "Components/Section";
import { SmallIcon } from "Components/SmallIcon";
import { WhatIsThisModal } from "Components/WhatIsThisModal";
import React from "react";
import {
  FitnessInstructorShowQuoteModel,
  PaymentRowModel,
  QuoteRowModel,
} from "SharedConstants/Schema";
import { BriefPolicyInformation } from "Views/Shared/BriefPolicyInformation";
import { Heading } from "../../Components/Heading";
import { MVCForm, MVCFormServerProps, MVCSubmitButton } from "../MVCForm";

export function ShowQuote(
  props: FitnessInstructorShowQuoteModel & {
    formProps: MVCFormServerProps;
  },
) {
  const insuranceName = props.ProgramNameForQuote.replace(" Insurance", "");
  useDocumentTitle(
    `${insuranceName} Insurance Quote - ${props.TitleProps.PageTitleSuffix}`,
  );
  return (
    <>
      <Heading
        {...props}
        title={
          <>
            {insuranceName && (
              <span className="is-inline-block">{insuranceName}</span>
            )}{" "}
            Insurance Quote{props.IsRenewal && " (Renewal Policy)"}
          </>
        }
      />
      <BriefPolicyInformation {...props.BriefPolicyInformationProps} />
      <div style={{ maxWidth: "40em", margin: "auto", marginTop: "1.5em" }}>
        {props.LiabilityCoverageBreakdown.length > 0 && (
          <Section title="Liability Coverages">
            {props.LiabilityCoverageBreakdown.map(section => (
              <React.Fragment key={section.Label}>
                <div
                  className="columns is-mobile is-vcentered is-centered"
                  style={{ marginBottom: "1.4em" }}
                >
                  <strong>{section.Label}</strong>
                </div>
                {section.Breakdown.map(e => (
                  <CoverageRow key={e.CoverageName} {...e} />
                ))}
              </React.Fragment>
            ))}
          </Section>
        )}
        {props.PaymentBreakdown.length > 0 && (
          <Section title="Premiums">
            {props.PaymentBreakdown.map((e, i) => (
              <CostRow key={i} {...e} />
            ))}
          </Section>
        )}
        <KeyValueRow
          className={{ "has-text-weight-bold": true }}
          style={{ fontSize: "1.1em", marginTop: "1em", marginBottom: "1em" }}
          name="Total Payment Amount"
          value={props.TotalAmountText}
        />
        <div className="has-text-centered" style={{ marginBottom: "2em" }}>
          <span className="premiumNote">{props.EarnedPremiumNote}</span>
        </div>
      </div>
      <div className="has-text-centered" style={{ marginBottom: "1em" }}>
        <InfoModal
          modalTitle={props.ProgramNameForQuote + " - Inclusions"}
          buttonCaption="Inclusions / Program Highlights"
        >
          <div dangerouslySetInnerHTML={props.QuoteInclusions} />
        </InfoModal>
      </div>
      <div className="has-text-centered" style={{ marginBottom: "1em" }}>
        <InfoModal
          modalTitle={props.ProgramNameForQuote + " - Exclusions"}
          buttonCaption="View Exclusions"
        >
          <div
            className="exclusions"
            dangerouslySetInnerHTML={props.CoverageExclusions}
          />
        </InfoModal>
      </div>
      <MVCForm {...props.formProps}>
        {({ isSubmitting }) => (
          <>
            {props.ShowSaveQuoteButton && (
              <>
                <div
                  className="has-text-centered"
                  style={{ marginBottom: "0.75em" }}
                >
                  <MVCSubmitButton className="button" value="save">
                    <SmallIcon name="fa-save" />
                    <span>Save Quote</span>
                  </MVCSubmitButton>
                </div>
                <div
                  className="has-text-centered"
                  style={{ marginBottom: "1.5em" }}
                >
                  All quotes are valid for {props.QuoteValidityPeriod} days.
                </div>
              </>
            )}
            <BulmaNavButtons
              backCaption="Back"
              continueCaption="Proceed to Payment"
              isDisabled={isSubmitting}
            />
          </>
        )}
      </MVCForm>
    </>
  );
}

function CostRow(props: PaymentRowModel) {
  return (
    <KeyValueRow
      className="has-background-light"
      name={props.PriceComponent}
      value={
        <>
          {props.PriceAndDescription}{" "}
          {props.WhatIsThis && (
            <WhatIsThisModal modalTitle={props.WhatIsThis.WhatIsThisTitle}>
              {props.WhatIsThis.WhatIsThisText}
            </WhatIsThisModal>
          )}
        </>
      }
    />
  );
}

function CoverageRow(props: QuoteRowModel) {
  return (
    <KeyValueRow
      className={{
        "has-background-light": true,
        "has-text-weight-bold": props.ShouldBold,
      }}
      name={props.CoverageName}
      value={props.Amount}
    />
  );
}

function KeyValueRow(props: {
  className?: ClassValue;
  style?: React.CSSProperties;
  name: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div
      className={classnames("columns is-mobile is-vcentered", props.className)}
      style={{ marginBottom: "1.4em", ...props.style }}
    >
      <div className="column has-text-right">{props.name}</div>
      <div className="column">{props.value}</div>
    </div>
  );
}
